import React from 'react';
import { useWatch } from 'react-hook-form';
import { FormRow } from '@cad-arc';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';
import { EntityPanelFormVehicleBaseKey, EntityPanelVehiclesForm } from '../types';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../../../../forms/integrated-form-controls/form-text-area';
import { EntityPanelMakeAndModelFields } from './entity-panel-make-and-model-fields';
import { EventPanelOptions, CadNextNotificationTypeEnumType } from '@mark43/cad-api';
import { useFormHasHadDataSetAtLeastOnceContext } from '../../../../event-panel/event-state/form-has-had-data-set-at-least-once';

interface EntityPanelFormBaseVehicleInputsProps {
    index: number;
    baseKey: EntityPanelFormVehicleBaseKey;
    entityPanelOptions: EventPanelOptions;
    isReadOnly?: boolean;
    userManuallyAddedField?: boolean;
}

export function EntityPanelFormBaseVehicleInputs(props: EntityPanelFormBaseVehicleInputsProps) {
    const { isFirstResponder } = useApplicationType();
    const lengthForFieldWithChangableSize = isFirstResponder ? 'md' : 'sm';
    const formHasHadDataSetAtLeastOnce = useFormHasHadDataSetAtLeastOnceContext();
    const { form } = useIntegratedForm<EntityPanelVehiclesForm>();
    const { index, entityPanelOptions, baseKey, isReadOnly, userManuallyAddedField } = props;
    const isPersisted = useWatch({
        control: form.control,
        name: `${baseKey}.${index}.isPersisted`,
    });
    const addedViaNarrative = useWatch({
        control: form.control,
        name: `vehicles.${index}.addedViaNarrative`,
    });

    const shouldAutoFocusPlateNumberInput =
        !addedViaNarrative && formHasHadDataSetAtLeastOnce && userManuallyAddedField;

    const getEventNotificationName = (name: string): CadNextNotificationTypeEnumType => {
        const base = baseKey === 'vehicles' || baseKey === 'mergedVehicles' ? '' : 'TOW_';
        return `${base}${name}` as CadNextNotificationTypeEnumType;
    };

    return (
        <>
            <FormRow>
                <FormInput
                    control={form.control}
                    label="Plate #"
                    name={`${baseKey}.${index}.plateNumber`}
                    asEditable={!!isPersisted}
                    length="sm"
                    eventNotificationName={getEventNotificationName('VEHICLE_PLATE_NUMBER')}
                    mode={isReadOnly ? 'read-only' : undefined}
                    autoFocus={shouldAutoFocusPlateNumberInput}
                />
                <FormSelect
                    control={form.control}
                    label="State"
                    length="sm"
                    options={entityPanelOptions.stateOptions}
                    name={`${baseKey}.${index}.stateOption`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('VEHICLE_STATE')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
                <FormInput
                    length="md"
                    control={form.control}
                    label="VIN"
                    name={`${baseKey}.${index}.vinNumber`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('VEHICLE_VIN_NUMBER')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
            </FormRow>
            <FormRow>
                <FormSelect
                    length={lengthForFieldWithChangableSize}
                    control={form.control}
                    label="Color"
                    options={entityPanelOptions.itemColorOptions}
                    name={`${baseKey}.${index}.colorOption`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('VEHICLE_COLOR')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
                <EntityPanelMakeAndModelFields
                    length={lengthForFieldWithChangableSize}
                    baseKey={baseKey}
                    isPersisted={!!isPersisted}
                    index={index}
                    isReadOnly={isReadOnly}
                />
                <FormInput
                    length={lengthForFieldWithChangableSize}
                    control={form.control}
                    label="Year"
                    name={`${baseKey}.${index}.year`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('VEHICLE_YEAR')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
            </FormRow>
            <FormRow>
                <FormInput
                    length="md"
                    control={form.control}
                    label="Insurance Carrier"
                    name={`${baseKey}.${index}.autoInsuranceCarrier`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName(
                        'VEHICLE_AUTO_INSURANCE_CARRIER'
                    )}
                />
                <FormTextarea
                    length="md"
                    control={form.control}
                    label="Notes"
                    name={`${baseKey}.${index}.notes`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('VEHICLE_NOTES')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
            </FormRow>
        </>
    );
}
